import { AttachmentDto, FileDto } from '@mark43/cad-api';
import { CombinedMediaItem, AttachmentSortOptionValue } from '../types';
import {
    ATTACHMENT_CACHE_MAX_AGE,
    IMAGE_FILE_EXTENSIONS,
    VIDEO_FILE_EXTENSIONS,
    AUDIO_FILE_EXTENSIONS,
    PDF_FILE_EXTENSION,
} from '../constants';
import { modals } from '@cad-arc';
import { logError } from '../../monitoring/log';
import { getFilePath, isRmsStreamUrl } from '../../common/helpers/files';
import { getRealUrlPrefix } from '~/session';

// hardcoding mediaAttachmentsEnabled to true because this helper is only used by attachments functions in this module
// which are only called when attachments is enabled
const getAttachmentFilePath = (file: FileDto | undefined) => getFilePath(file, true);

export function logAttachmentsError(error?: Error, category?: string) {
    const err = error instanceof Error ? error : new Error('Unknown error');
    logError(err, {
        type: `attachments.${category ?? err?.name}`,
    });
}

export function getFileName(attachment: AttachmentDto): string {
    switch (attachment.attachmentType) {
        case 'IMAGE':
            if (attachment.image?.originalFile?.fileType === 'PDF') {
                return attachment.image?.originalFile?.originalFileName || 'Unknown pdf';
            }
            return attachment.image?.originalFile?.originalFileName || 'Unknown image';
        case 'VIDEO':
            return attachment.video?.originalFile?.originalFileName || 'Unknown video';
        case 'FILE':
            return attachment.file?.originalFileName || 'Unknown file';
        default:
            return attachment.attachmentType;
    }
}

export function getFileServerId(attachment: AttachmentDto): string | undefined {
    switch (attachment.attachmentType) {
        case 'IMAGE':
            return attachment.image?.originalFile?.fileServerId;
        case 'VIDEO':
            return attachment.video?.originalFile?.fileServerId;
        case 'FILE':
            return attachment.file?.fileServerId;
        default:
            return undefined;
    }
}

export function getFileSize(attachment: AttachmentDto): number | undefined {
    switch (attachment.attachmentType) {
        case 'IMAGE':
            return attachment.image?.originalFile?.contentLength;
        case 'VIDEO':
            return attachment.video?.originalFile?.contentLength;
        case 'FILE':
            return attachment.file?.contentLength;
        default:
            return undefined;
    }
}

/**
 * Get download URL for an attachment.
 * Returns fileWebServerPath for the attachment file.
 * @param attachment - The attachment object
 */
export function getDownloadUrl(attachment: AttachmentDto): string | undefined {
    switch (attachment.attachmentType) {
        case 'IMAGE':
            return getAttachmentFilePath(attachment.image?.originalFile);
        case 'VIDEO':
            return getAttachmentFilePath(attachment.video?.originalFile);
        case 'FILE':
            return getAttachmentFilePath(attachment.file);
        default:
            return undefined;
    }
}

export function getAttachmentIcon(
    attachment: AttachmentDto
): 'PDFDocument' | 'Image' | 'VideoFile' | 'Document' {
    switch (attachment.attachmentType) {
        case 'IMAGE':
            if (attachment.image?.originalFile?.fileType === 'PDF') {
                return 'PDFDocument';
            }
            return 'Image';
        case 'VIDEO':
            return 'VideoFile';
        case 'FILE':
            return 'Document';
        default:
            return 'Document';
    }
}

export function getVideoThumbnailUrl(previewFramePath: string | undefined): string {
    const prefix = getRealUrlPrefix();

    if (!previewFramePath || !prefix) {
        return '';
    }

    try {
        // URL will replace last path segment in base url if base ends w/o trailing slash
        const base = prefix.endsWith('/') ? prefix : `${prefix}/`;
        return new URL(previewFramePath, base).toString();
    } catch {
        return previewFramePath;
    }
}

function getImageUrl(attachment: AttachmentDto, maxPreviewSizeBytes: number) {
    const fileSizeBytes = attachment.image?.originalFile?.contentLength;
    const canPreviewImage =
        maxPreviewSizeBytes && fileSizeBytes && fileSizeBytes <= maxPreviewSizeBytes;

    if (canPreviewImage) {
        return getAttachmentFilePath(attachment.image?.originalFile) || '';
    } else {
        return '';
    }
}

function getImageThumbnailUrl(attachment: AttachmentDto, thumbnailMaxSizeBytes: number) {
    const thumbnailExists = (file: FileDto | undefined) => {
        return !!file?.fileWebServerPath;
    };
    // Priority 1: Use large thumbnail if available
    if (thumbnailExists(attachment.image?.thumbnailLargeFile)) {
        return getAttachmentFilePath(attachment.image?.thumbnailLargeFile);
    }

    // Priority 2: Use medium thumbnail if available
    if (thumbnailExists(attachment.image?.thumbnailMediumFile)) {
        return getAttachmentFilePath(attachment.image?.thumbnailMediumFile);
    }

    // Priority 3: Use small thumbnail for grid views if available
    if (thumbnailExists(attachment.image?.thumbnailSmallFile)) {
        return getAttachmentFilePath(attachment.image?.thumbnailSmallFile);
    }

    // Priority 4: Fallback to original if small enough
    const fileSizeBytes = attachment.image?.originalFile?.contentLength;
    const canUseOriginalFileAsThumbnailFallback =
        thumbnailMaxSizeBytes && fileSizeBytes ? fileSizeBytes <= thumbnailMaxSizeBytes : false;

    if (canUseOriginalFileAsThumbnailFallback) {
        return getAttachmentFilePath(attachment.image?.originalFile) || '';
    } else {
        return '';
    }
}

/**
 * Determines the preview URL for a PDF based on file size constraints.
 * Returns empty string if file is too large to preview.
 */
function getPdfPreviewUrl(attachment: AttachmentDto, maxPreviewSizeBytes: number): string {
    const fileSizeBytes = attachment.image?.originalFile?.contentLength;
    const originalUrl = getAttachmentFilePath(attachment.image?.originalFile) || '';

    // If no size or size unknown, allow preview
    if (!fileSizeBytes) {
        return originalUrl;
    }

    // Check if file is within size limit
    const canPreview = maxPreviewSizeBytes && fileSizeBytes <= maxPreviewSizeBytes;

    return canPreview ? originalUrl : '';
}

/**
 * Determines the preview URL for a video based on file size constraints.
 * Returns empty string if file is too large to preview.
 */
function getVideoPreviewUrl(attachment: AttachmentDto, maxPreviewSizeBytes: number): string {
    const fileSizeBytes = attachment.video?.originalFile?.contentLength;
    const file = attachment.video?.originalFile;

    // If no size or size unknown, allow preview
    if (!fileSizeBytes) {
        return getAttachmentFilePath(file);
    }

    // Check if file is within size limit
    const canPreview = maxPreviewSizeBytes && fileSizeBytes <= maxPreviewSizeBytes;

    return canPreview ? getAttachmentFilePath(file) : '';
}

/**
 * Determines if an attachment can be previewed based on its type and file size.
 * Returns true if the file size is within limits or size is unknown.
 */
export function canPreviewAttachment(
    type: CombinedMediaItem['type'],
    fileSizeBytes: number | undefined,
    previewSizeLimits: PreviewSizeLimits
): boolean {
    // Unknown size, allow preview attempt
    if (!fileSizeBytes) return true;

    // Type-specific size limit checking
    switch (type) {
        case 'pdf':
            return fileSizeBytes <= previewSizeLimits.pdfMaxPreviewSizeBytes;
        case 'image':
            return fileSizeBytes <= previewSizeLimits.imageMaxPreviewSizeBytes;
        case 'video':
            return fileSizeBytes <= previewSizeLimits.videoMaxPreviewSizeBytes;
        case 'audio':
        default:
            return true; // Audio and generic files always previewable
    }
}

/**
 * Extract file extension from filename and map to arc FileItem fileType
 */
export function getFileTypeFromExtension(fileName: string): CombinedMediaItem['type'] {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    if (extension === PDF_FILE_EXTENSION) {
        return 'pdf';
    }
    if ((IMAGE_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
        return 'image';
    }

    if ((VIDEO_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
        return 'video';
    }

    if ((AUDIO_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
        return 'audio';
    }

    return;
}

/**
 * Sort attachments based on sort option
 */
export function sortAttachments(
    attachments: AttachmentDto[],
    sortOption: AttachmentSortOptionValue
): AttachmentDto[] {
    const sorted = [...attachments];

    switch (sortOption) {
        case 'uploadDate-newest':
            return sorted.sort(
                (a, b) =>
                    new Date(b.createdDateUtc).getTime() - new Date(a.createdDateUtc).getTime()
            );
        case 'uploadDate-oldest':
            return sorted.sort(
                (a, b) =>
                    new Date(a.createdDateUtc).getTime() - new Date(b.createdDateUtc).getTime()
            );
        case 'name-az':
            return sorted.sort((a, b) => getFileName(a).localeCompare(getFileName(b)));
        case 'name-za':
            return sorted.sort((a, b) => getFileName(b).localeCompare(getFileName(a)));
        case 'size-largest':
            return sorted.sort((a, b) => (getFileSize(b) ?? 0) - (getFileSize(a) ?? 0));
        case 'size-smallest':
            return sorted.sort((a, b) => (getFileSize(a) ?? 0) - (getFileSize(b) ?? 0));
        default:
            return sorted;
    }
}

export interface PreviewSizeLimits {
    imageMaxPreviewSizeBytes: number;
    pdfMaxPreviewSizeBytes: number;
    videoMaxPreviewSizeBytes: number;
    thumbnailMaxSizeBytes: number;
}

/**
 * Convert CAD backend AttachmentDto to CombinedMediaItem
 */
export function convertAttachmentsToCombinedItems({
    attachments,
    previewSizeLimits,
}: {
    attachments: AttachmentDto[];
    previewSizeLimits: PreviewSizeLimits;
}): CombinedMediaItem[] {
    const {
        imageMaxPreviewSizeBytes,
        pdfMaxPreviewSizeBytes,
        videoMaxPreviewSizeBytes,
        thumbnailMaxSizeBytes,
    } = previewSizeLimits;
    return attachments.map((attachment) => {
        const title = getFileName(attachment);
        const fileSizeBytes = getFileSize(attachment);

        let combinedMediaItem: CombinedMediaItem | undefined;
        let isThumbnailCacheable = true;
        if (attachment.attachmentType === 'IMAGE') {
            const fileType = attachment.image?.originalFile?.fileType;
            const attachmentUrl = getDownloadUrl(attachment) || '';

            if (fileType === 'PDF') {
                combinedMediaItem = {
                    id: attachment.attachmentId.toString(),
                    type: 'pdf',
                    src: getPdfPreviewUrl(attachment, pdfMaxPreviewSizeBytes),
                    thumbnailSrc: getAttachmentFilePath(attachment.image?.thumbnailLargeFile) || '',
                    title,
                    fileSizeBytes,
                    attachmentUrl,
                };
            } else {
                combinedMediaItem = {
                    id: attachment.attachmentId.toString(),
                    src: getImageUrl(attachment, imageMaxPreviewSizeBytes),
                    thumbnailSrc: getImageThumbnailUrl(attachment, thumbnailMaxSizeBytes),
                    title,
                    type: 'image',
                    fileSizeBytes,
                    attachmentUrl,
                };
            }
        } else if (attachment.attachmentType === 'VIDEO') {
            const previewFramePath = attachment.video?.previewFrameWebPaths?.[0];
            const attachmentUrl = getDownloadUrl(attachment) || '';
            // the video thumbnail is not from /files/stream/{fille_server_id} endpoint, do not add max-age for video thumbnailSrc
            isThumbnailCacheable = false;
            const videoSrc = getVideoPreviewUrl(attachment, videoMaxPreviewSizeBytes);
            const videoBase = {
                id: attachment.attachmentId.toString(),
                src: videoSrc,
                thumbnailSrc: getVideoThumbnailUrl(previewFramePath),
                title,
                fileSizeBytes,
                attachmentUrl,
            };

            combinedMediaItem = isRmsStreamUrl(videoSrc)
                ? { ...videoBase, type: 'htmlVideo' }
                : { ...videoBase, type: 'video' };
        } else if (attachment.attachmentType === 'FILE') {
            const fileName = attachment.file?.originalFileName || '';
            const fileType = getFileTypeFromExtension(fileName);
            const fileSrc = getAttachmentFilePath(attachment.file) || '';
            // Use htmlAudio for RMS stream URLs — BE response format is incompatible with vidstack playback
            const resolvedType =
                fileType === 'audio' && isRmsStreamUrl(fileSrc) ? 'htmlAudio' : fileType;

            return {
                id: attachment.attachmentId.toString(),
                src: fileSrc,
                thumbnailSrc: '',
                title,
                type: resolvedType,
                fileSizeBytes,
                attachmentUrl: fileSrc,
            };
        } else {
            combinedMediaItem = {
                id: attachment.attachmentId.toString(),
                src: '',
                thumbnailSrc: '',
                title,
                type: undefined,
                attachmentUrl: '',
            };
        }
        if (
            isThumbnailCacheable &&
            combinedMediaItem?.thumbnailSrc &&
            isRmsStreamUrl(combinedMediaItem.thumbnailSrc)
        ) {
            // ?max-age is only supported for rms stream url, attempting to use on presigned url will throw 500
            combinedMediaItem.thumbnailSrc = `${combinedMediaItem.thumbnailSrc}?max-age=${ATTACHMENT_CACHE_MAX_AGE}`;
        }

        // video uses a presigned url already has cache-control configured and will break if url is appended with query string
        if (
            combinedMediaItem?.type !== 'video' &&
            combinedMediaItem?.src &&
            isRmsStreamUrl(combinedMediaItem.src)
        ) {
            // ?max-age is only supported for rms stream url, attempting to use on presigned url will throw 500
            combinedMediaItem.src = `${combinedMediaItem.src}?max-age=${ATTACHMENT_CACHE_MAX_AGE}`;
        }

        return combinedMediaItem;
    });
}

export const shouldExportAttachments = (): Promise<boolean> => {
    return new Promise((resolve) => {
        modals.confirm({
            body: 'Do you want to include attachments in this export?',
            title: 'Export PDF with Attachments',
            confirmLabel: 'Yes',
            cancelLabel: 'No',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
};

export interface GroupedMergedAttachments {
    eventId: number;
    attachments: AttachmentDto[];
}

export function groupMergedAttachmentsByEvent(
    mergedAttachments: AttachmentDto[]
): GroupedMergedAttachments[] {
    const grouped = new Map<number, AttachmentDto[]>();

    mergedAttachments.forEach((attachment) => {
        const eventId = attachment.entityId;
        if (!grouped.has(eventId)) {
            grouped.set(eventId, []);
        }
        const attachments = grouped.get(eventId);
        if (attachments) attachments.push(attachment);
    });

    return Array.from(grouped.entries()).map(([eventId, attachments]) => ({
        eventId,
        attachments,
    }));
}
